import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Spinner, Tabs } from "flowbite-react";

import { Sidenav, AccountStates } from "../../components";
import { useAccount } from "../../hooks/useAccount";
import { useAuth } from "../../hooks/useAuth";
import { useAIInsights } from "../../hooks/useAIInsights";
import { useActions } from "../../hooks/useActions";
import { useExtractionStatus } from "../../hooks/useExtractionStatus";
import { useToast } from "../../hooks/useToast";
import { useUsageSummary } from "../../hooks/useUsageSummary";
import { useAnalytics } from "../../hooks/useAnalytics";
import { NOTIFICATION_TYPES } from "../../utils";
import { alertFeedbackService } from "../../services/alertFeedbackService";
import {
  transformAccount,
  transformForecast,
  transformAISummary,
  transformAlertToSignal,
  transformUsage,
} from "./transforms";
import { CustomerHeader } from "./components/CustomerHeader";
import { ClientOverviewCard } from "./components/ClientOverviewCard";
import { RenewalForecastCard } from "./components/RenewalForecastCard";
import { SlackChannelCard } from "./components/SlackChannelCard";
import { FeatureFlag } from "../../components/FeatureFlag";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsContext";
import { AISummaryCard } from "./components/AISummaryCard";
import {
  ExtractionProgressCard,
  EmailCountBadge,
} from "./components/ExtractionProgressCard";
import { SignalsSection } from "./components/SignalsSection";
import { CustomerGoalsSection } from "./components/CustomerGoalsSection";
import { ProductUsageSection } from "./components/ProductUsageSection";
import { ExpansionFeatures } from "./components/ExpansionFeatures";
import { ExpansionOrganizational } from "./components/ExpansionOrganizational";
import { ProductUsageTab } from "./ProductUsageComponents";

const SectionSkeleton = ({ label }) => (
  <div className="flex items-center justify-center rounded-xl border border-gray-200 bg-white px-5 py-8">
    <Spinner size="sm" className="mr-2" />
    <span className="text-sm text-gray-400">Loading {label}…</span>
  </div>
);

// Listable lifecycle states — fetched together so the tabs show full counts.
// Snooze is NOT a backend status: snoozed signals come back under "active"
// (status active + future snooze_until) and the transform derives them into
// the Snoozed tab, so we don't request "snoozed" here.
// Rejected = feedback_state "dismissed" (status is usually "active", and can be
// "resolved"), so they are not selected by status: the page opts in with
// includeRejected (sends include_rejected=1) and the transform puts them in
// the Rejected tab.
// Stable module-level reference (useActions requires it for its deps).
const SIGNAL_STATUS_FILTER = ["active", "resolved"];

const ErrorPage = ({ message }) => (
  <div className="flex h-full w-full flex-row">
    <Sidenav />
    <div className="flex h-screen flex-1 items-center justify-center bg-gray-50">
      <div className="text-center">
        <p className="text-lg font-semibold text-gray-900">
          {message ?? "Something went wrong"}
        </p>
        <p className="mt-1 text-sm text-gray-500">
          Check the URL and try again.
        </p>
      </div>
    </div>
  </div>
);

export const CustomerDetailsPage = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { apiClient, user } = useAuth();
  const showToast = useToast();

  // Data fetching — all hooks run in parallel
  const {
    account,
    loading: accountLoading,
    error: accountError,
    patchAccount,
    refetch: refetchAccount,
  } = useAccount(id);
  const {
    insights,
    isLoading: aiLoading,
    isGenerating,
    isRevalidating,
    fetchInsights,
  } = useAIInsights(id);
  const {
    actions: allAlerts,
    loading: actionsLoading,
    refetch: refetchActive,
    updateActionItem,
  } = useActions(id, {
    statusFilter: SIGNAL_STATUS_FILTER,
    includeRejected: true,
  });

  // On-demand extraction — triggers when emails haven't been extracted yet
  const { extractionStatus, total, extracted, reason, lookbackDays } =
    useExtractionStatus(id, {
      onReady: () => {
        // Extraction done — refetch AI insights and signals
        if (fetchInsights) fetchInsights();
        if (refetchActive) refetchActive();
      },
    });
  const { summary: usageSummary, loading: usageLoading } = useUsageSummary(id);

  const { trackEvent } = useAnalytics();

  useEffect(() => {
    if (!account?.id) return;
    trackEvent("account_viewed", {
      account_id: account.id,
      segment: account.segment ?? null,
      health_score: account.health_score ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.id, trackEvent]);

  const aiInsightsViewedRef = useRef(false);
  const aiSummaryStateRef = useRef(null);

  // Fetch contracts for ExpansionFeatures
  const [contracts, setContracts] = useState([]);
  const fetchContracts = useCallback(async () => {
    if (!id) return;
    try {
      const res = await apiClient.get(`accounts/${id}/contracts/`);
      setContracts(res.data?.results ?? res.data ?? []);
    } catch {
      // non-critical — expansion features degrade gracefully
    }
  }, [apiClient, id]);
  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const [signalsGenerating, setSignalsGenerating] = useState(false);

  // Lifecycle overrides — maps alertId → updated lifecycle fields.
  // Applied optimistically so a signal moves tabs immediately, then
  // reconciled with the BE response; cleared on refetch.
  const [lifecycleOverrides, setLifecycleOverrides] = useState({});

  // Combined object matching shape expected by ExpansionFeatures
  const customerData = account ? { ...account, id, contracts } : null;

  // Transforms — only when data is available
  const accountProps = account ? transformAccount(account) : null;
  const forecastProps = account ? transformForecast(account) : null;
  const aiSummary = transformAISummary(insights);

  // Reset guard when navigating to a different account
  useEffect(() => {
    aiInsightsViewedRef.current = false;
  }, [id]);

  // Fire once per account when summary loads
  useEffect(() => {
    if (!aiSummary || aiInsightsViewedRef.current) return;
    aiInsightsViewedRef.current = true;
    trackEvent("ai_insights_viewed", { account_id: id });
  }, [id, aiSummary, trackEvent]);

  // ai_summary_shown — the #18 detector. Mirrors the render branches below:
  // "fallback_badge" on a mature account is exactly the blank-AI-summary
  // signature. Transient loading/generating states are never recorded; the
  // latest event per account view carries the final state (alert dashboards
  // filter on account age to ignore genuinely-new accounts).
  const aiSummaryState =
    extractionStatus !== "ready" && extractionStatus !== "loading"
      ? "extraction_progress"
      : aiLoading || isGenerating
        ? null
        : aiSummary
          ? "present"
          : extracted > 0
            ? "fallback_badge"
            : "absent";

  useEffect(() => {
    aiSummaryStateRef.current = null;
  }, [id]);

  useEffect(() => {
    if (!aiSummaryState || aiSummaryStateRef.current === aiSummaryState) return;
    aiSummaryStateRef.current = aiSummaryState;
    trackEvent("ai_summary_shown", {
      account_id: id,
      state: aiSummaryState,
      account_created_at: account?.created_at ?? null,
    });
  }, [aiSummaryState, id, trackEvent, account]);

  const signals = (allAlerts ?? []).map((alert) => {
    const signal = transformAlertToSignal(alert);
    const override = lifecycleOverrides[alert.id];
    return override ? { ...signal, ...override } : signal;
  });
  const usageProps = usageSummary
    ? transformUsage(usageSummary, account)
    : null;

  // Error state — account is critical, everything else degrades gracefully
  if (accountError) {
    return <ErrorPage message={accountError} />;
  }

  // ---- Signal lifecycle handlers ----
  // Each applies an optimistic override (so the signal moves tabs at once),
  // then reconciles with the BE response. On failure the override is dropped
  // and a toast surfaces the error.
  const setOverride = (alertId, fields) =>
    setLifecycleOverrides((prev) => ({
      ...prev,
      [alertId]: { ...prev[alertId], ...fields },
    }));

  const clearOverride = (alertId) =>
    setLifecycleOverrides((prev) => {
      const next = { ...prev };
      delete next[alertId];
      return next;
    });

  const runLifecycle = async ({ alertId, optimistic, call, errorMsg }) => {
    const previous = lifecycleOverrides[alertId];
    setOverride(alertId, {
      ...optimistic,
      lifecycleBy: user ?? null,
      lifecycleAt: new Date().toISOString(),
    });
    try {
      const updated = await call();
      // BE exposes per-action attribution fields (no generic status_changed_*).
      setOverride(alertId, {
        lifecycleBy:
          updated?.snoozed_by_name ??
          updated?.resolved_by_name ??
          updated?.feedback_by_name ??
          user ??
          null,
        lifecycleAt:
          updated?.snoozed_at ??
          updated?.resolved_at ??
          updated?.feedback_at ??
          new Date().toISOString(),
        ...(updated?.resolved_automatically !== undefined
          ? { resolvedAutomatically: updated.resolved_automatically === true }
          : {}),
        ...(updated?.snooze_until !== undefined
          ? { snoozedUntil: updated.snooze_until }
          : {}),
      });
    } catch (err) {
      console.error(errorMsg, err);
      // Roll back to the prior override state.
      setLifecycleOverrides((prev) => ({ ...prev, [alertId]: previous }));
      if (!previous) clearOverride(alertId);
      showToast({ content: errorMsg, type: NOTIFICATION_TYPES.error });
    }
  };

  const handleResolveSignal = (alertId) =>
    runLifecycle({
      alertId,
      optimistic: { status: "resolved" },
      call: () => alertFeedbackService.resolveAlert(alertId),
      errorMsg: "Failed to resolve signal",
    });

  const handleSnoozeSignal = (alertId, snoozedUntil) =>
    runLifecycle({
      alertId,
      optimistic: { status: "snoozed", snoozedUntil },
      call: () => alertFeedbackService.snoozeAlert(alertId, snoozedUntil),
      errorMsg: "Failed to snooze signal",
    });

  const handleRejectSignal = (alertId, reason, note) =>
    runLifecycle({
      alertId,
      optimistic: { status: "rejected", rejectReason: reason },
      call: () => alertFeedbackService.dismissAlert(alertId, reason, note),
      errorMsg: "Failed to reject signal",
    });

  // Revert a terminal signal back to active. Only snoozed signals expose the
  // Reactivate button today (unsnooze); resolved/rejected need new BE endpoints
  // before their buttons can be surfaced (see TODO in SignalLifecycleActions).
  const handleReactivateSignal = (alertId) =>
    runLifecycle({
      alertId,
      optimistic: { status: "active", snoozedUntil: null },
      call: () => alertFeedbackService.unsnoozeAlert(alertId),
      errorMsg: "Failed to reactivate signal",
    });

  const handleGenerateSignals = async () => {
    try {
      setSignalsGenerating(true);
      await apiClient.post(`accounts/${id}/generate-signals/`);
      showToast({
        content: "Signal generation started — results will appear shortly.",
        type: NOTIFICATION_TYPES.success,
      });
      // Poll for results after a short delay
      setTimeout(() => {
        refetchActive();
        if (fetchInsights) fetchInsights();
        setSignalsGenerating(false);
      }, 10000);
    } catch (err) {
      console.error("Failed to generate signals:", err);
      showToast({
        content: "Failed to generate signals",
        type: NOTIFICATION_TYPES.error,
      });
      setSignalsGenerating(false);
    }
  };

  const handleSaveForecastNote = async (noteText) => {
    try {
      await patchAccount({ forecast_note: noteText });
    } catch (err) {
      console.error("Failed to save forecast note:", err);
      showToast({
        content: "Failed to save forecast note",
        type: NOTIFICATION_TYPES.error,
      });
    }
  };

  return (
    <div className="flex h-full w-full flex-row">
      <Sidenav />
      <div className="h-screen flex-1 overflow-y-scroll bg-gray-50">
        {/* Header — shows skeleton while loading */}
        {accountLoading || !accountProps ? (
          <div className="border-b border-gray-200 bg-white px-6 py-6">
            <div className="mx-auto flex w-full max-w-7xl items-center gap-3">
              <Spinner size="sm" />
              <span className="text-sm text-gray-400">Loading account…</span>
            </div>
          </div>
        ) : (
          <CustomerHeader account={accountProps} />
        )}

        <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <Tabs aria-label="Customer details tabs" variant="underline">
            <Tabs.Item active title="Overview">
              <div className="space-y-6">
                {/* Overview cards */}
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {accountProps ? (
                    <ClientOverviewCard account={accountProps} />
                  ) : (
                    <SectionSkeleton label="client overview" />
                  )}
                  {forecastProps ? (
                    <RenewalForecastCard
                      forecast={forecastProps}
                      onSaveNote={handleSaveForecastNote}
                    />
                  ) : (
                    <SectionSkeleton label="renewal forecast" />
                  )}
                  {accountProps && (
                    <FeatureFlag flag={FEATURE_FLAGS.SLACK_CHANNEL_LINKING}>
                      <SlackChannelCard
                        accountId={id}
                        channelName={accountProps.slackChannelName}
                        onChanged={refetchAccount}
                      />
                    </FeatureFlag>
                  )}
                </div>

                {/* Customer goals — what success looks like for this account */}
                {accountProps && (
                  <div className="animate-fade-in">
                    <CustomerGoalsSection
                      accountId={id}
                      customerName={accountProps.name}
                    />
                  </div>
                )}

                {/* AI Summary — show extraction progress when emails not yet analyzed */}
                {extractionStatus !== "ready" &&
                extractionStatus !== "loading" ? (
                  <div className="animate-fade-in">
                    <ExtractionProgressCard
                      total={total}
                      extracted={extracted}
                      status={extractionStatus}
                      reason={reason}
                      lookbackDays={lookbackDays}
                    />
                  </div>
                ) : aiLoading || isGenerating ? (
                  <SectionSkeleton
                    label={
                      isGenerating ? "AI analysis (generating…)" : "AI summary"
                    }
                  />
                ) : aiSummary ? (
                  <div className="animate-fade-in">
                    <AISummaryCard summary={aiSummary} />
                    {/* Always mounted so the live region exists before its text changes —
                        a region that mounts already populated is often not announced. */}
                    <p
                      className={
                        isRevalidating
                          ? "mt-1 flex items-center gap-1 px-1 text-xs text-gray-500"
                          : "sr-only"
                      }
                      role="status"
                      aria-live="polite"
                    >
                      {isRevalidating && (
                        <>
                          <Spinner size="xs" aria-hidden="true" />
                          {t("pages.customer.aiSummary.updating")}
                        </>
                      )}
                    </p>
                    {extracted > 0 && (
                      <div className="mt-1 px-1">
                        <EmailCountBadge
                          extracted={extracted}
                          lookbackDays={lookbackDays}
                        />
                      </div>
                    )}
                  </div>
                ) : extracted > 0 ? (
                  <div className="animate-fade-in px-1">
                    <EmailCountBadge
                      extracted={extracted}
                      lookbackDays={lookbackDays}
                    />
                  </div>
                ) : null}

                {/* Signals */}
                {extractionStatus !== "ready" &&
                extractionStatus !== "loading" ? (
                  <div className="animate-fade-in rounded-xl border border-gray-200 bg-white px-5 py-4">
                    <p className="text-sm text-gray-500">
                      Signals will appear after email analysis completes.
                    </p>
                  </div>
                ) : actionsLoading ? (
                  <SectionSkeleton label="signals" />
                ) : (
                  <div className="animate-fade-in">
                    <SignalsSection
                      signals={signals}
                      hubspotSync={account?.hubspot_signal_sync ?? null}
                      onUpdateActionItem={updateActionItem}
                      onResolve={handleResolveSignal}
                      onSnooze={handleSnoozeSignal}
                      onReject={handleRejectSignal}
                      onReactivate={handleReactivateSignal}
                      onSignalViewed={(signal) =>
                        trackEvent("alert_viewed", {
                          alert_id: signal.id,
                          alert_type: signal.alertType ?? null,
                        })
                      }
                    />
                    {signals.length === 0 && extracted > 0 && (
                      <div className="mt-2 flex items-center gap-3 px-1">
                        <p className="text-sm text-gray-500">
                          No signals found.
                        </p>
                        <button
                          onClick={handleGenerateSignals}
                          disabled={signalsGenerating}
                          className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          {signalsGenerating
                            ? "Generating…"
                            : "Generate Signals"}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Account States */}
                <AccountStates accountId={id} />

                {/* Product Usage Summary */}
                {usageLoading ? (
                  <SectionSkeleton label="product usage" />
                ) : usageProps ? (
                  <ProductUsageSection usage={usageProps} />
                ) : null}
              </div>
            </Tabs.Item>

            <Tabs.Item title="Product Usage">
              <ProductUsageTab accountId={id} />
            </Tabs.Item>

            <Tabs.Item title="Expansion">
              <div className="space-y-6">
                {customerData ? (
                  <>
                    <ExpansionFeatures customerData={customerData} />
                    <ExpansionOrganizational />
                  </>
                ) : (
                  <SectionSkeleton label="expansion" />
                )}
              </div>
            </Tabs.Item>
          </Tabs>
        </div>
      </div>
    </div>
  );
};
